import { lazy } from 'react';
import { Navigate } from 'react-router';

import { withBottomNav } from '@perpay-web/components/higher-order/WithBottomNav/WithBottomNav';
import { withLoader } from '@perpay-web/components/higher-order/WithLoader/WithLoader';
import { withPerpayTitle } from '@perpay-web/components/higher-order/WithPerpayTitle/WithPerpayTitle';
import { withRedirectIfIncomplete } from '@perpay-web/components/higher-order/WithRedirectIfIncomplete/WithRedirectIfIncomplete';
import { withRedirectIfUnauthenticated } from '@perpay-web/components/higher-order/WithRedirectIfUnauthenticated/WithRedirectIfUnauthenticated';
import CardOnboardingSwitcherContainer from '@perpay-web/fintech/components/containers/CardOnboardingSwitcherContainer';
import SpendingLimitLandingContainer from '@perpay-web/fintech/components/containers/SpendingLimitLandingContainer';
import { withBlankHeader } from '@perpay-web/fintech/components/higher-order/WithBlankHeader/WithBlankHeader';
import { RouteLevelErrorComponent } from '@perpay-web/fintech/components/util/ErrorBoundary';
import {
    IS_PAYCHEX_LAUNCHED,
    IS_SELF_SERVICE_EDIT_ADDRESS_LAUNCHED,
    IS_SELF_SERVICE_EDIT_EMAIL_LAUNCHED,
    IS_SELF_SERVICE_EDIT_PHONE_LAUNCHED,
} from '@perpay-web/fintech/constants/flags';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { importBankAccountAddContainer } from '@perpay-web/fintech/utils/imports/importBankAccountAddContainer';
import { importBankAccountVerificationContainer } from '@perpay-web/fintech/utils/imports/importBankAccountVerificationContainer';
import { importBankAccountVerifySelectContainer } from '@perpay-web/fintech/utils/imports/importBankAccountVerifySelectContainer';
import { importCardActivationContainer } from '@perpay-web/fintech/utils/imports/importCardActivationContainer';
import { importCardActivityScreenContainer } from '@perpay-web/fintech/utils/imports/importCardActivityScreenContainer';
import { importCardCreateAutoPayContainer } from '@perpay-web/fintech/utils/imports/importCardCreateAutoPayContainer';
import { importCardDetailsContainer } from '@perpay-web/fintech/utils/imports/importCardDetailsContainer';
import { importCardFreezeContainer } from '@perpay-web/fintech/utils/imports/importCardFreezeContainer';
import { importCardLearnContainer } from '@perpay-web/fintech/utils/imports/importCardLearnContainer';
import { importCardSettingsContainer } from '@perpay-web/fintech/utils/imports/importCardSettingsContainer';
import { importCardStatementsScreenContainer } from '@perpay-web/fintech/utils/imports/importCardStatementsContainer';
import { importCheckoutContainer } from '@perpay-web/fintech/utils/imports/importCheckoutContainer';
import { importCreditScoreDetailsContainer } from '@perpay-web/fintech/utils/imports/importCreditScoreDetailsContainer';
import { importCreditScoresContainer } from '@perpay-web/fintech/utils/imports/importCreditScoresContainer';
import { importDashboardContainer } from '@perpay-web/fintech/utils/imports/importDashboardContainer';
import { importDirectDepositActivityContainer } from '@perpay-web/fintech/utils/imports/importDirectDepositActivityContainer';
import { importDirectDepositDetailsContainer } from '@perpay-web/fintech/utils/imports/importDirectDepositDetailsContainer';
import { importEditDefaultAddressContainer } from '@perpay-web/fintech/utils/imports/importEditDefaultAddressContainer';
import { importEditEmailContainer } from '@perpay-web/fintech/utils/imports/importEditEmailContainer';
import { importEditEmployment } from '@perpay-web/fintech/utils/imports/importEditEmployment';
import { importEditPhoneContainer } from '@perpay-web/fintech/utils/imports/importEditPhoneContainer';
import { importExternalCardAdd } from '@perpay-web/fintech/utils/imports/importExternalCardAdd';
import { importIdentityVerificationContainer } from '@perpay-web/fintech/utils/imports/importIdentityVerificationContainer';
import { importMarketplaceActivityContainer } from '@perpay-web/fintech/utils/imports/importMarketplaceActivityContainer';
import { importMarketplaceCardApplicationContainer } from '@perpay-web/fintech/utils/imports/importMarketplaceCardApplicationContainer';
import { importMarketplaceDetailsContainer } from '@perpay-web/fintech/utils/imports/importMarketplaceDetailsContainer';
import { importMarketplaceErrorScreenContainer } from '@perpay-web/fintech/utils/imports/importMarketplaceErrorScreenContainer';
import { importMarketplaceLearnContainer } from '@perpay-web/fintech/utils/imports/importMarketplaceLearnContainer';
import { importOneTimePaymentPortalContainer } from '@perpay-web/fintech/utils/imports/importOneTimePaymentPortalContainer';
import { importOrders } from '@perpay-web/fintech/utils/imports/importOrders';
import {
    importOverpaymentResolvedSuccess,
    importUserInitiatedWithdrawalSuccess,
} from '@perpay-web/fintech/utils/imports/importOverpaymentResolvedSuccess';
import { importPartnerOnboardingContainer } from '@perpay-web/fintech/utils/imports/importPartnerOnboardingContainer';
import { importPaymentPortalContainer } from '@perpay-web/fintech/utils/imports/importPaymentPortalContainer';
import { importPaymentSetupContainer } from '@perpay-web/fintech/utils/imports/importPaymentSetupContainer';
import { importPayrollConnectionIntro } from '@perpay-web/fintech/utils/imports/importPayrollConnectionIntro';
import { importPayrollConnectionSuccess } from '@perpay-web/fintech/utils/imports/importPayrollConnectionSuccess';
import { importPayrollInstructions } from '@perpay-web/fintech/utils/imports/importPayrollInstructions';
import { importPerpayPlusSoloApplicationContainer } from '@perpay-web/fintech/utils/imports/importPerpayPlusSoloApplicationContainer';
import { importPerpayPlusSoloCheckoutContainer } from '@perpay-web/fintech/utils/imports/importPerpayPlusSoloCheckoutContainer';
import { importProfile } from '@perpay-web/fintech/utils/imports/importProfile';
import { importRecurringPaymentPortalContainer } from '@perpay-web/fintech/utils/imports/importRecurringPaymentPortalContainer';
import { importReferralsContainer } from '@perpay-web/fintech/utils/imports/importReferralsContainer';
import { importReturnPortal } from '@perpay-web/fintech/utils/imports/importReturnPortal';
import { importRewardsAndCreditsContainer } from '@perpay-web/fintech/utils/imports/importRewardsAndCreditsContainer';
import { importRewardsAndCreditsDetailsContainer } from '@perpay-web/fintech/utils/imports/importRewardsAndCreditsDetailsContainer';
import { importRewardHistoryContainer } from '@perpay-web/fintech/utils/imports/importRewardsHistoryContainer';
import { importStandaloneUploadPaystubFlow } from '@perpay-web/fintech/utils/imports/importStandaloneUploadPaystubFlow';
import { importUpdateDirectDepositContainer } from '@perpay-web/fintech/utils/imports/importUpdateDirectDepositContainer';
import { importWithdrawalContainer } from '@perpay-web/fintech/utils/imports/importWithdrawalContainer';
import {
    importOverpaymentWithdrawalFlowContainer,
    importStandardUserInitiatedWithdrawalFlowContainer,
} from '@perpay-web/fintech/utils/imports/importWithdrawalFlowContainer';
import { importWithdrawalSwitcherContainer } from '@perpay-web/fintech/utils/imports/importWithdrawalSwitcherContainer';
import { compose } from '@perpay-web/utils/functionalUtils';

const EmptyBottomNav = () => null;
const withAuthenticatedRouteBehavior = compose(
    withRedirectIfIncomplete,
    withRedirectIfUnauthenticated,
);

const CheckoutRoute = compose(
    withPerpayTitle('Checkout'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importCheckoutContainer)));

const MarketplaceErrorScreenRoute = compose(
    withPerpayTitle('Checkout Error'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importMarketplaceErrorScreenContainer)));

const PayrollInstructionsRoute = compose(
    withBlankHeader,
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importPayrollInstructions)));

const PaymentSetupRoute = compose(
    withPerpayTitle('Payment Setup'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importPaymentSetupContainer)));

const ReferralsRoute = compose(
    withPerpayTitle('Referrals'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importReferralsContainer)));

const IdentityVerificationRoute = compose(
    withPerpayTitle('Verify Identity'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importIdentityVerificationContainer)));

const BankAccountAddRoute = compose(
    withPerpayTitle('Add Your Bank'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importBankAccountAddContainer)));

const BankAccountVerificationRoute = compose(
    withPerpayTitle('Verify Bank Account'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importBankAccountVerificationContainer)));

const ExternalCardAddWithLoader = compose(
    withPerpayTitle('Add Your Card'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importExternalCardAdd)));

const StandaloneUploadPaystubFlowWithLoader = compose(
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importStandaloneUploadPaystubFlow)));

const OneTimePaymentPortalRoute = compose(
    withPerpayTitle('Payment Portal'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importOneTimePaymentPortalContainer)));

const RecurringPaymentPortalRoute = compose(
    withPerpayTitle('Payment Portal'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importRecurringPaymentPortalContainer)));

const PaymentPortalRoute = compose(
    withPerpayTitle('Payment Portal'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importPaymentPortalContainer)));

const WithdrawalSwitcherRoute = compose(
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importWithdrawalSwitcherContainer)));

const WithdrawalRoute = compose(
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importWithdrawalContainer)));

const UserInitiatedWithDrawalFlowRoute = compose(
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importStandardUserInitiatedWithdrawalFlowContainer)));

const WithdrawalFlowRoute = compose(
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importOverpaymentWithdrawalFlowContainer)));

const OverpaymentResolvedSuccessRoute = compose(
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importOverpaymentResolvedSuccess)));

const UserInitiatedWithdrawalSuccessRoute = compose(
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importUserInitiatedWithdrawalSuccess)));

const PayrollConnectionSuccessRoute = compose(
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importPayrollConnectionSuccess)));

const PayrollConnectionIntroRoute = compose(
    withBottomNav(EmptyBottomNav),
    withRedirectIfUnauthenticated,
)(withLoader(lazy(importPayrollConnectionIntro)));

const BankAccountVerifySelectRoute = compose(
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importBankAccountVerifySelectContainer)));

const EditEmploymentWithLoader = compose(
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importEditEmployment)));

const CardDetailsRoute = compose(
    withPerpayTitle('Card Details'),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importCardDetailsContainer)));

const CardCreateAutoPayRoute = compose(
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importCardCreateAutoPayContainer)));

const CardFreezeRoute = compose(
    withPerpayTitle('Freeze Card'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importCardFreezeContainer)));

const CardStatementsScreenRoute = compose(
    withPerpayTitle('Statements'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importCardStatementsScreenContainer)));

const CardSettingsRoute = compose(
    withPerpayTitle('Card Settings'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importCardSettingsContainer)));

const DirectDepositDetailsRoute = compose(
    withPerpayTitle('Direct Deposit Details'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importDirectDepositDetailsContainer)));

const UpdateDirectDepositRoute = compose(
    withPerpayTitle('Update Your Payroll Direct Deposit'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importUpdateDirectDepositContainer)));

const RewardsAndCreditsRoute = compose(
    withPerpayTitle('Rewards & Credits'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importRewardsAndCreditsContainer)));

const RewardsAndCreditsDetailsRoute = compose(
    withPerpayTitle('Rewards & Credits'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importRewardsAndCreditsDetailsContainer)));

const RewardHistoryRoute = compose(
    withPerpayTitle('Reward History'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importRewardHistoryContainer)));

const PerpayPlusSoloCheckoutRoute = compose(
    withPerpayTitle('Perpay+'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importPerpayPlusSoloCheckoutContainer)));

const CardActivationContainer = compose(
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importCardActivationContainer)));

const MarketplaceActivityRoute = compose(
    withPerpayTitle('Marketplace Activity'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importMarketplaceActivityContainer)));

const DirectDepositActivityRoute = compose(
    withPerpayTitle('Direct Deposit Activity'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importDirectDepositActivityContainer)));

const DashboardRoute = compose(
    withPerpayTitle('Dashboard'),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importDashboardContainer)));

const OrdersWithLoader = compose(
    withPerpayTitle('Orders'),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importOrders)));

const ProfileWithLoader = compose(
    withPerpayTitle('Profile'),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importProfile)));

const EditDefaultAddressWithLoader = compose(
    withBlankHeader,
    withPerpayTitle('Edit Default Address'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importEditDefaultAddressContainer)));

const EditEmailWithLoader = compose(
    withPerpayTitle('Edit Email'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importEditEmailContainer)));

const EditPhoneWithLoader = compose(
    withPerpayTitle('Edit Phone'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importEditPhoneContainer)));

const CardActivityScreenContainer = compose(
    withPerpayTitle('Card Activity'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importCardActivityScreenContainer)));

const CardOnboardingContainer = compose(
    withPerpayTitle('Perpay Card'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(CardOnboardingSwitcherContainer));

const CardLearnContainer = compose(
    withPerpayTitle('Perpay Card'),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importCardLearnContainer)));

const MarketplaceDetailsRoute = compose(
    withPerpayTitle('Marketplace Details'),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importMarketplaceDetailsContainer)));

const MarketplaceLearnRoute = compose(
    withPerpayTitle('Marketplace'),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importMarketplaceLearnContainer)));

const CreditScoresRoute = compose(
    withPerpayTitle('Credit Monitoring'),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importCreditScoresContainer)));

const CreditScoreDetailsRoute = compose(
    withPerpayTitle('Score History'),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importCreditScoreDetailsContainer)));

const PartnerOnboardingApplicationRoute = compose(
    withPerpayTitle('Partner Onboarding'),
    withBottomNav(EmptyBottomNav),
    withRedirectIfUnauthenticated,
)(withLoader(lazy(importPartnerOnboardingContainer)));

const MarketplacePartnerOnboardingApplicationRoute = compose(
    withPerpayTitle('Marketplace Partner Onboarding'),
    withBottomNav(EmptyBottomNav),
    withRedirectIfUnauthenticated,
)(withLoader(lazy(importMarketplaceCardApplicationContainer)));

const PerpayPlusSoloApplicationRoute = compose(
    withPerpayTitle('Perpay+ Application'),
    withBottomNav(EmptyBottomNav),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importPerpayPlusSoloApplicationContainer)));

const ReturnPortalRoute = compose(
    withPerpayTitle('Return Portal'),
    withAuthenticatedRouteBehavior,
)(withLoader(lazy(importReturnPortal)));

const SpendingLimitRoute = compose(
    withPerpayTitle('Spending Limit'),
    withBottomNav(EmptyBottomNav),
    withRedirectIfUnauthenticated,
)(withLoader(SpendingLimitLandingContainer));

/**
 * @type {import('react-router').RouteObject[]}
 */
export const authenticatedRoutes = [
    {
        path: paths.bankVerifySelect.path,
        element: <BankAccountVerifySelectRoute />,
    },
    {
        path: paths.withdrawalEntry.path,
        element: <WithdrawalSwitcherRoute />,
    },
    { path: paths.withdrawal.path, element: <WithdrawalRoute /> },
    {
        path: paths.userInitiatedWithdrawal.path,
        element: <UserInitiatedWithDrawalFlowRoute />,
    },
    {
        path: paths.overpaymentWithdrawal.path,
        element: <WithdrawalFlowRoute />,
    },
    {
        path: paths.overpaymentResolved.path,
        element: <OverpaymentResolvedSuccessRoute />,
    },
    {
        path: paths.withdrawalSuccess.path,
        element: <UserInitiatedWithdrawalSuccessRoute />,
    },
    { path: paths.dashboard.path, element: <DashboardRoute /> },
    {
        path: paths.editEmployment.path,
        element: <EditEmploymentWithLoader />,
    },
    { path: `${paths.profile.path}/*`, element: <ProfileWithLoader /> },
    IS_SELF_SERVICE_EDIT_ADDRESS_LAUNCHED
        ? {
              path: `${paths.editDefaultAddress.path}/*`,
              element: <EditDefaultAddressWithLoader />,
          }
        : {},
    // TODO: Remove when the edit email flow is released
    IS_SELF_SERVICE_EDIT_EMAIL_LAUNCHED
        ? {
              path: paths.editEmail.path,
              element: <EditEmailWithLoader />,
          }
        : {},
    IS_SELF_SERVICE_EDIT_PHONE_LAUNCHED
        ? {
              path: paths.editPhone.path,
              element: <EditPhoneWithLoader />,
          }
        : {},
    { path: paths.orders.path, element: <OrdersWithLoader /> },
    { path: `${paths.checkout.path}:uuid`, element: <CheckoutRoute /> },
    {
        path: paths.checkoutError.path,
        element: <MarketplaceErrorScreenRoute />,
    },
    {
        path: paths.paymentInstructions.path,
        element: <PayrollInstructionsRoute />,
    },
    { path: paths.paymentPortal.path, element: <PaymentPortalRoute /> },
    {
        path: paths.recurringPayments.path,
        element: <RecurringPaymentPortalRoute />,
        errorElement: <RouteLevelErrorComponent />,
    },
    {
        path: paths.oneTimePayments.path,
        element: <OneTimePaymentPortalRoute />,
        errorElement: <RouteLevelErrorComponent />,
    },
    { path: paths.paymentSetup.path, element: <PaymentSetupRoute /> },
    { path: paths.referrals.path, element: <ReferralsRoute /> },
    {
        path: paths.identityVerification.path,
        element: <IdentityVerificationRoute />,
    },
    { path: paths.bankCreate.path, element: <BankAccountAddRoute /> },
    {
        path: `${paths.bankVerify.path}:uuid`,
        element: <BankAccountVerificationRoute />,
    },
    {
        path: paths.externalCardCreate.path,
        element: <ExternalCardAddWithLoader />,
        errorElement: <RouteLevelErrorComponent />,
    },
    {
        path: paths.uploadPaystub.path,
        element: <StandaloneUploadPaystubFlowWithLoader />,
    },
    { path: paths.creditMonitoring.path, element: <CreditScoresRoute /> },
    {
        path: paths.creditMonitoringDetails.path,
        element: <CreditScoreDetailsRoute />,
    },
    {
        path: paths.perpayPlus.path,
        element: <PerpayPlusSoloCheckoutRoute />,
    },
    {
        path: paths.cardActivate.path,
        element: <CardActivationContainer />,
    },
    {
        path: paths.cardOnboarding.path,
        element: <CardOnboardingContainer />,
    },
    { path: paths.cardDetails.path, element: <CardDetailsRoute /> },
    { path: paths.cardFreeze.path, element: <CardFreezeRoute /> },
    { path: paths.cardLearn.path, element: <CardLearnContainer /> },
    {
        path: paths.cardActivity.path,
        element: <CardActivityScreenContainer />,
    },
    { path: paths.cardPayment.path, element: <CardCreateAutoPayRoute /> },
    {
        path: paths.cardStatements.path,
        element: <CardStatementsScreenRoute />,
    },
    { path: paths.cardSettings.path, element: <CardSettingsRoute /> },
    {
        path: paths.directDeposit.path,
        element: <DirectDepositDetailsRoute />,
    },
    {
        path: paths.updateDirectDeposit.path,
        element: <UpdateDirectDepositRoute />,
    },
    {
        path: paths.updateDirectDepositCreditLimit.path,
        element: <UpdateDirectDepositRoute />,
    },
    {
        path: paths.rewardsAndCredits.path,
        element: <RewardsAndCreditsRoute />,
    },
    {
        path: paths.marketplaceDetails.path,
        element: <MarketplaceDetailsRoute />,
    },
    {
        path: paths.marketplaceLearn.path,
        element: <MarketplaceLearnRoute />,
    },
    {
        path: paths.rewardsAndCreditsDetails.path,
        element: <RewardsAndCreditsDetailsRoute />,
    },
    { path: paths.rewardHistory.path, element: <RewardHistoryRoute /> },
    {
        path: paths.marketplaceActivity.path,
        element: <MarketplaceActivityRoute />,
    },
    {
        path: paths.directDepositActivity.path,
        element: <DirectDepositActivityRoute />,
    },
    {
        path: paths.creditReporting.path,
        element: <Navigate to={paths.perpayPlus.path} />,
    },
    {
        path: paths.bankVerify.path,
        element: <Navigate to={paths.dashboard.path} />,
    },
    {
        path: paths.partnerOnboardingApplication.path,
        element: <PartnerOnboardingApplicationRoute />,
    },
    {
        path: paths.marketplacePartnerOnboardingApplication.path,
        element: <MarketplacePartnerOnboardingApplicationRoute />,
    },
    {
        path: paths.perpayPlusSoloApplication.path,
        element: <PerpayPlusSoloApplicationRoute />,
    },
    { path: paths.returnPortal.path, element: <ReturnPortalRoute /> },
    { path: paths.spendingLimit.path, element: <SpendingLimitRoute /> },
    ...(IS_PAYCHEX_LAUNCHED
        ? [
              {
                  path: paths.payrollConnectionSuccess.path,
                  element: <PayrollConnectionSuccessRoute />,
              },
              {
                  path: paths.payrollConnectionIntro.path,
                  element: <PayrollConnectionIntroRoute />,
              },
          ]
        : []),
];
